import * as React from 'react';
import {LiteralUnion} from 'type-fest';

import {emptyObject} from '@packages/utilities/fp/utils';
import {emotionStyled} from '@packages/utilities/styled';

import {tokens} from '../../themes';
import type {TextColor} from '../../components/Text';

export type IconSize = LiteralUnion<
  8 | 10 | 12 | 14 | 16 | 20 | 24 | 32 | 64 | 72,
  string
>;

type TextOpacity = 'default' | 'low' | 'minimum';

const styleTokens = {
  color: '--component-icon-color',
};

export interface BaseIconProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'color'
> {
  /**
   * Custom label for assistive technology devices
   */
  ['aria-label']?: string;
  /**
   * Custom label (associated with another element) for assistive technology devices
   */
  ['aria-labelledby']?: string;
  /**
   * The color style of the icon
   */
  colorVariant?: TextColor | 'default';
  /**
   * The opacity of an icon
   */
  contrast?: TextOpacity;
  /**
   * id used for jest test identification
   */
  ['data-automation-id']?: string;
  /**
   * Determines the density of the Icon's padding.
   */
  density?: 'comfortable' | 'compact';
  /**
   * Determines the size of the icon.
   */
  size?: IconSize;
  /**
   * Determines if the icon should use styled system to assign aria-attributes.
   */
  shouldBypassAria?: boolean;
}

export interface IconProps extends BaseIconProps {
  children: React.ReactNode;
}

const IconView = emotionStyled('div')(
  {
    color: `var(${styleTokens.color}, currentColor)`,
    /**
     * This resolves a situation where an SVG inside of a Flex parent will squish and squash.
     * We're effectively saying this div should not shrink, should not grow, but should stay the Goldilocks SizeVar
     *
     * See MDN docs: https://developer.mozilla.org/en-US/docs/Web/CSS/flex#values
     */
    flex: 'none',
    svg: {
      display: 'block',
      width: '100%',
      height: '100%',
    },
  },
  {
    componentName: 'Icon',
    variants: {
      colorVariant: {
        inherit: {
          '&&&&': {
            /**
             * Being explicit here to override any highly specific styles up
             * the chain (e.g hoverSelectors).
             *
             * @see https://webflow.atlassian.net/browse/DS-497
             */
            color: 'currentColor !important',
          },
        },
        /**
         * Base colors
         */
        default: {
          color: '$textSecondary',
        },
        primary: {
          '&&&&': {
            color: '$textPrimary',
          },
        },
        secondary: {
          '&&&&': {
            color: '$textSecondary',
          },
        },
        dimmed: {
          '&&&&': {
            color: '$textDimmed',
          },
        },
        inverse: {
          '&&&&': {
            color: '$textInverse',
          },
        },
        /**
         * Stateless colors
         * Brand icon tokens are theme-dependent: focus in light mode, outline in dark mode.
         */
        blue: {
          '&&&&': {
            color: '$blueIcon',

            /* NOTE (from @Dustin-Webflow's pull request comment https://github.com/webflow/webflow/pull/98577#discussion_r2823746551)
             * Here we are applying theme values for our branded icons from within Spring. This is a departure from what we've been doing, which is having SpringBranded be encapsulated to its own Directory.
             * IMHO, until we formally adopt SpringBranded into Spring, I think a better approach might be to create a new BrandedIcon component that composes from this IconView and injects its own theme-aware logic (if necessary).
             */
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-blue-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-blue-text)',
              },
          },
        },
        green: {
          '&&&&': {
            color: '$greenIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-green-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-green-text)',
              },
          },
        },
        orange: {
          '&&&&': {
            color: '$orangeIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-orange-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-orange-text)',
              },
          },
        },
        pink: {
          '&&&&': {
            color: '$pinkIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-pink-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-pink-text)',
              },
          },
        },
        purple: {
          '&&&&': {
            color: '$purpleIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-purple-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-purple-text)',
              },
          },
        },
        yellow: {
          '&&&&': {
            color: '$yellowIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-yellow-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-yellow-text)',
              },
          },
        },
        /**
         * Stateful colors
         */
        danger: {
          '&&&&': {
            color: '$redIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-red-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-red-text)',
              },
          },
        },
        success: {
          '&&&&': {
            color: '$greenIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-green-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-green-text)',
              },
          },
        },
        warning: {
          '&&&&': {
            color: '$yellowIcon',
            '[data-theme="light"] &': {
              '--colors-brand-icon-focus': 'var(--colors-yellow-text)',
            },
            '[data-theme="darkDefault"] &, [data-theme="darkBrighter"] &, [data-theme="darkDarker"] &':
              {
                '--colors-brand-icon-outline': 'var(--colors-yellow-text)',
              },
          },
        },
        /**
         * Deprecated colors
         */
        active: {
          '&&&&': {
            color: '$textPrimary',
          },
        },
        heading: {
          '&&&&': {
            color: '$textPrimary',
          },
        },
      },
      contrast: {
        default: {
          opacity: tokens.opacities.default,
        },
        low: {
          opacity: tokens.opacities.low,
        },
        minimum: {
          opacity: tokens.opacities.minimum,
        },
      },
      density: {
        comfortable: {
          padding: tokens.ui.iconButton.padding.comfortable,
        },
        compact: {
          padding: tokens.ui.iconButton.padding.compact,
        },
      },
    },
    defaultVariants: {
      contrast: 'default',
    },
  }
);

const IconComponent = React.forwardRef<HTMLDivElement, IconProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      children,
      colorVariant,
      contrast = 'default',
      density,
      size: sizeProp = 16,
      style = emptyObject,
      ...rest
    }: IconProps,
    ref
  ) => {
    /**
     * The Icon should be hidden from screen readers (i.e. aria-hidden="true")
     * if neither ariaLabel nor ariaLabelledBy are provided.
     */
    const isHidden = !(ariaLabel || ariaLabelledBy);

    const padding =
      density === 'comfortable'
        ? tokens.ui.iconButton.padding.comfortable
        : tokens.ui.iconButton.padding.compact;

    let size = sizeProp;

    if (density !== undefined) {
      if (typeof sizeProp === 'number') {
        // Convert the number to a string with px for calc() to work
        size = `${sizeProp}px`;
      }

      /**
       * We have to calculate the height and width of the Icon based on the padding
       * because the Icon has `box-sizing: border-box` applied.
       *
       * Otherwise, if we apply padding without adjusting the height and width,
       * the Icon will appear smaller than expected.
       */
      size = `calc(2 * ${padding} + ${size})`;
    }

    return (
      <IconView
        {...rest}
        aria-hidden={isHidden}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        colorVariant={colorVariant}
        contrast={contrast}
        density={density}
        ref={ref}
        style={{
          width: size,
          height: size,
          ...style,
        }}
      >
        {children}
      </IconView>
    );
  }
);

IconComponent.displayName = 'Icon';
IconComponent.toString = IconView.toString;

/**
 * TypeScript solution for adding tokens statically to the Icon component.
 *
 * @see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-1008349828
 *
 * Added { tokens } to provide better ergonomics to customizing the Icon
 * color for certain UI setups.
 *
 * @see https://webflow.atlassian.net/browse/PDTY-7091
 * 2023-12-05
 */
export const Icon = Object.assign({}, IconComponent, {
  __experimentalTokens: styleTokens,
});

export type SpringIconType = typeof IconComponent;

export default Icon;
